import { FC, useState, useEffect, useMemo } from 'react'
import { FutureLayerUID, HaveChildLayerHint, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema, useHints } from 'components/features/future/helpers'
import { useFormContext } from 'react-hook-form'
import { FutureWhenInput } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { maskCurrencyPositive } from 'components/atoms/inputs/maskedInput'
import { schemaNumber } from 'schemas/baseSchema'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'have_child' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  spent_on_child: schemaNumber,
}) as ObjectSchema<LayerType>)

type FutureLayerPaneHaveChildProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneHaveChild: FC<FutureLayerPaneHaveChildProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layer }
      slug={ LAYER_SLUG }
      name='Have a Child'
      icon='child'
      type='goal'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FormFields uid={ uid } />
    </FuturePanePreset>
  )
}

type FormFieldsProps = {
  uid?: FutureLayerUID
}
const FormFields: FC<FormFieldsProps> = (props) => {
  const { uid } = props
  const { getValues, setValue } = useFormContext<LayerType>()

  const errors = useCollectLayerErrors<LayerType>()

  const [taxRateHints, setTaxRateHints] = useState<HaveChildLayerHint['hints'] | null>(null)
  useHints<'have_child'>(setTaxRateHints, ['slug'])

  const amountNote = useMemo(() => {
    if (!taxRateHints) {
      return
    }

    return `Americans spend ${ +taxRateHints.income_percent_spent_on_child }% of their income on each child on average. For you, that will be ${ formatUSCurrency(taxRateHints.income_spent_on_child) } per month`
  }, [taxRateHints])

  useEffect(() => {
    const amount = getValues('spent_on_child')
    if (!taxRateHints || amount !== undefined) {
      return
    }

    setValue('spent_on_child', taxRateHints.income_spent_on_child)
  }, [taxRateHints, getValues, setValue])

  const completeChild = useMemo(() => (
    <h1>
      Goal Achieved. Congratulations!
    </h1>
  ), [])

  return (
    <LayerStatus uid={ uid || null } completeContent={ completeChild } skipPartialState type='goal'>
      <FutureWhenInput error={ errors.whenField } />

      <FutureFieldSlider
        label='Amount spent monthly on Child'
        name='spent_on_child'
        mask={ maskCurrencyPositive }

        error={ errors.spent_on_child }
        note={ amountNote }

        min={ 0 }
        max={ 5000 }
        step={ 5 }
      />
    </LayerStatus>
  )
}
